/* eslint-disable @typescript-eslint/no-unused-expressions */
import { getMainAudience } from '@packages/cms-components';
import config, { getFullLocale } from '@packages/config/dist';
import { createUrqlServerClient } from '@packages/gql';
import { ProductRatingDataDocument } from '@packages/gql/generated/shopping/ProductRatingDataDocument';
import { ReviewsDataDocument } from '@packages/gql/generated/shopping/ReviewsDataDocument';
import { CompactProduct, RatingPageHead } from '@packages/product';
import { AddRating } from '@packages/product/src/components/CustomerRatings/AddRating';
import { ProductOverview } from '@packages/product/src/components/CustomerRatings/ProductOverview';
import { RatingInfo } from '@packages/product/src/components/CustomerRatings/RatingInfo';
import { RatingList } from '@packages/product/src/components/CustomerRatings/RatingList';
import { RatingTags } from '@packages/product/src/components/CustomerRatings/RatingTags';
import { ReviewStatistic } from '@packages/product/src/components/CustomerRatings/ReviewStatistic';
import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  RatingIcons,
  SlotBox,
  Stack,
  Typography,
  useDeviceType,
  useI18n,
} from '@packages/shared';
import { ArrowMediumLeft32 } from '@packages/themes/icons';
import { useQuery } from 'urql';
import { useIntl } from 'react-intl';
import { useEffect, useMemo } from 'react';
import { PageMetaData } from '@packages/modules';
import { parseCookies } from 'nookies';
import { getEcState, logger, useConfig } from '@packages/utilities';
import type { GetServerSideProps, NextPage } from 'next';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { promotionBannerDataFetcher } from '@packages/cms-components/src/modules/PromotionBanner/utils/fetch/promotionBannerDataFetcher';

/* GraphQL */ `
  query ProductRatingData(
    $productId: String!
    $locale: String!
    $skipAplus: Boolean! = false
    $lang: String!
  ) {
    product(id: $productId, locale: $locale) {
      name
      akl
      sku
      slug
      # needed for aPlus federation-query
      itemOptionCommKey
      # needed for aPlus federation-query
      hasServices
      ...AddRatingFragment
      ...CompactProductFragment
      ...RatingPageSeoFragment
      ...HeadlineFragment
      ...ProductOverviewFragment
      ...RatingTagSkuFragment
      seoRating(locale: $locale) {
        product {
          avgRating
          reviewCount
          reviews {
            ...ReviewFragment
          }
          tags {
            ...RatingTagsFragment
          }
          reviewStatistics {
            ...ReviewStatisticFragment
          }
        }
        verifiedRatingsHintLayerId
      }
    }
  }
`;

const RatingPage: NextPage = () => {
  const { replace } = useRouter();
  const searchParams = useSearchParams();
  const params = useParams<{ akl: string; slug: string }>();

  const { language, defaultLanguage, localizeUrl } = useI18n();

  const sku = searchParams?.get('sku') || '';

  const { isMobile } = useDeviceType();
  const { formatMessage, locale } = useIntl();
  const {
    product: {
      aplus: { enabled: aplusEnabled },
    },
  } = useConfig();

  const [{ data }] = useQuery({
    query: ProductRatingDataDocument,
    variables: {
      productId: sku || ((params?.akl ?? params?.slug) as string),
      locale,
      lang: language || defaultLanguage!,
      skipAplus: !aplusEnabled,
    },
    context: useMemo(() => ({ suspense: false }), []),
  });

  const { product } = { ...data };
  const { seoRating } = { ...product };
  const { product: ratingProduct } = { ...seoRating };

  useEffect(() => {
    if (product && !params?.akl) {
      replace(
        localizeUrl(
          `/p/${product.slug}/${product.akl}/rating?${searchParams?.has('sku') ? searchParams?.toString() : `sku=${product.sku}&${searchParams?.toString()}`}`,
          language,
        ),
        { scroll: false },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  if (!product) {
    return null;
  }

  const productLink = `/p/${product.slug}/${product.akl}?${searchParams?.toString()}`;

  return (
    <Box>
      {/* needs to be first component in page render flow because of timing issues with glycerin tracking */}
      <PageMetaData page={{ type: 'detailview', template: 'rating' }} />
      <RatingPageHead data={product} />
      <Grid container rowSpacing={2} columnSpacing={5} sx={{ px: 1.5 }}>
        {/* TODO: improve styling */}
        <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
          <Button
            startIcon={<ArrowMediumLeft32 />}
            variant="text"
            sx={{ p: 0 }}
            LinkComponent={Link}
            href={productLink}
          >
            {formatMessage({
              defaultMessage: 'Zurück zum Produkt',
              id: 'customer-ratings.back-to-product',
            })}
          </Button>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            component="h1"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              typography: ['h5', 'h5', 'h3'],
            }}
          >
            {formatMessage({
              defaultMessage: 'Alle Bewertungen zum Produkt',
              id: 'customer-ratings.overview.headline',
            })}
            {seoRating && (
              <RatingInfo verifiedRatingsHintLayerId={seoRating.verifiedRatingsHintLayerId} />
            )}
          </Typography>
          {!isMobile ? (
            <Link
              href={productLink}
              underline="none"
              sx={{ color: 'black', '&:hover': { color: 'primary.main' } }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  fontWeight: 'fontWeightSemiBold',
                }}
              >
                {product.name}
              </Typography>
            </Link>
          ) : (
            <CompactProduct data={product} withProductName showTaxHint={false} />
          )}
          {ratingProduct && (
            <RatingIcons
              avgRating={ratingProduct.avgRating}
              reviewCount={ratingProduct.reviewCount}
              sx={{ mb: -1, mt: 1.5 }}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {ratingProduct && (
            <>
              <Stack spacing={0.5} mb={3}>
                {ratingProduct.reviewStatistics.map((rs, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ReviewStatistic data={rs} key={index} />
                ))}
              </Stack>
              <RatingTags ratingTagsData={ratingProduct.tags} skuData={product} />
            </>
          )}
        </Grid>
        {!isMobile && (
          <Grid item xs={12} md={6}>
            <ProductOverview productData={product} />
          </Grid>
        )}
      </Grid>
      <SlotBox
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 3 }}
      >
        <AddRating productData={product} />
        {ratingProduct && <RatingList />}
      </SlotBox>
    </Box>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { res, query, req } = context;
  const { akl, sku, slug, ftag, 'nav-c': navC, lang } = query;
  const language = lang as string;
  // set response cache-control header to use public caching
  res?.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const { ssrCache, ssrClient } = createUrqlServerClient();
  const locale = getFullLocale(language);

  const productQuery = ssrClient
    .query(ProductRatingDataDocument, {
      productId: ((sku || (akl ?? slug)) as string)!,
      locale: locale!,
      skipAplus: !config.product.aplus.enabled,
      lang: language || '',
    })
    .toPromise();

  const ratingsQuery = ssrClient
    .query(ReviewsDataDocument, {
      productId: ((akl ?? slug) as string)!,
      locale: locale!,
      start: 0,
      count: config.customerRatings.pagingCount,
      filter: (ftag && (Array.isArray(ftag) ? ftag[0] : ftag).replace('f-', '')) || null,
    })
    .toPromise();

  const [fetchedPromotionData, productData, ratingsData] = await Promise.allSettled([
    promotionBannerDataFetcher(
      { ...query, ...{ shopId: typeof navC === 'string' ? navC : undefined } },
      parseCookies({ req }),
      context,
      locale,
    ),
    productQuery,
    ratingsQuery,
  ]);
  const audience = req.cookies ? getMainAudience(getEcState(req.cookies)) : 'anonymous';
  const promotionBannerCacheKey = `promotionBannerData-${language}-${audience}`;

  if (productData.status === 'rejected' || productData.value.data?.product === null) {
    logger.error(
      productData.status === 'rejected' ? productData.reason : productData.value.error,
      `Error during fetching product (akl: ${akl} sku: ${sku})`,
    );

    if (productData.status === 'rejected' || productData.value.data?.product === null) {
      return {
        notFound: true,
      };
    }
  }

  if (ratingsData.status === 'rejected' || ratingsData.value.error) {
    logger.error(
      'Error during fetching data for ratings page: ',
      ratingsData.status === 'rejected' ? ratingsData.reason : ratingsData.value.error,
    );
  }

  if (fetchedPromotionData.status === 'rejected') {
    logger.error(
      { description: 'Promotion Banner Data Fetch (Rating-Page)' },
      `Could not fetch data ${typeof navC === 'string' ? `for ID ${navC}` : ''}`,
    );
  }

  return {
    props: {
      urqlState: ssrCache.extractData(),
      fallback: {
        [promotionBannerCacheKey]:
          fetchedPromotionData.status === 'fulfilled' ? fetchedPromotionData.value : null,
      },
    },
  };
};

// eslint-disable-next-line import/no-default-export
export default RatingPage;
